import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · Warn-Kachel „N Objekte ohne Concierge".
 *
 * User-Directive: „Zeig eine Kachel „N Objekte ohne Concierge" oben im
 * Dashboard, damit die Lücke jedem Admin beim Login auffällt."
 *
 * Design-Prinzip:
 *   • Rendert sich NUR wenn count > 0 — kein Clutter, wenn alles sauber ist.
 *   • Rote Grundfarbe (Grau→Blau-Regel-Konsistenz): fehlende Zuweisung ist
 *     dieselbe Kategorie wie „Handlungsbedarf".
 *   • Bis zu 3 Beispielnamen als Untertitel („z. B. Villa Pfaff, …").
 *   • Deep-Link zu /team/properties mit Query `?filter=unassigned` (Client
 *     filtert Client-side, kein neuer Backend-Handler nötig).
 *   • Nur für Admins sichtbar (Backend gibt 403 für Concierge).
 */
export default function PropertiesWithoutConciergeCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    (async () => {
      try {
        const { data } = await api.get("/team/properties/unassigned-count");
        setData(data);
      } catch (e) {
        // Silently fail — Widget ist optional, nicht kritisch.
        setData({ count: 0, sample_names: [] });
      }
    })();
  }, [user?.id, user?.role]);

  // Nichts rendern bei fehlender Rolle, nicht geladen, oder count = 0
  if (user?.role !== "admin") return null;
  if (data === null) return null;
  if (!data.count || data.count === 0) return null;

  const sampleText = (data.sample_names || []).slice(0, 3).join(", ");
  return (
    <Link
      to="/team/properties?filter=unassigned"
      data-testid="properties-without-concierge-card"
      data-count={data.count}
      className="block bg-white border-l-4 p-5 md:p-6 mb-6 hover:border-l-[6px] transition-all group"
      style={{
        borderLeftColor: STATUS_COLORS.red,
        borderTop: `1px solid ${withAlpha(STATUS_COLORS.red, 0.15)}`,
        borderRight: `1px solid ${withAlpha(STATUS_COLORS.red, 0.15)}`,
        borderBottom: `1px solid ${withAlpha(STATUS_COLORS.red, 0.15)}`,
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
            style={{ background: withAlpha(STATUS_COLORS.red, 0.12), color: STATUS_COLORS.red }}
          >
            <AlertTriangle className="w-5 h-5" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="label-caps mb-1"
              style={{ color: STATUS_COLORS.red }}
            >
              Handlungsbedarf · Concierge-Zuweisung
            </div>
            <h3 className="font-display text-xl md:text-2xl text-navy leading-snug">
              {data.count === 1
                ? "1 Objekt ohne zugewiesenen Concierge"
                : `${data.count} Objekte ohne zugewiesenen Concierge`}
            </h3>
            {sampleText && (
              <p
                className="text-[13px] text-navy-dark/70 mt-2 leading-relaxed"
                data-testid="properties-without-concierge-samples"
              >
                z. B. {sampleText}
                {data.count > (data.sample_names?.length || 0) && " …"}
              </p>
            )}
            <p className="text-[12px] text-navy-dark/55 mt-2 max-w-xl leading-relaxed">
              Ohne Concierge bleibt die Grau→Blau-Timeline eingefroren, Buchungen
              können keinen Fallback-Verantwortlichen ziehen, Anreise-Radar zeigt
              keine Person. Jetzt zuweisen ↓
            </p>
          </div>
        </div>
        <div
          className="shrink-0 flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase font-medium group-hover:gap-3 transition-all"
          style={{ color: STATUS_COLORS.red }}
        >
          Zu Objekten
          <ArrowRight className="w-4 h-4" strokeWidth={2} />
        </div>
      </div>
    </Link>
  );
}
